<template>
  <div class="activity-box">
    <div class="activity-hero">
      <div class="activity-hero-toolbar">
        <div class="hero-heading">
          <div class="hero-heading-label">Activity Center</div>
          <div class="hero-heading-title">活动中心</div>
          <div class="hero-heading-subtitle">{{ activitySummaryText }}</div>
        </div>
      </div>

      <div class="hero-card" v-if="heroActivity.id" @click="goToDetail(heroActivity)">
        <span class="hero-status" :class="getStatusTagClass(heroActivity)">
          {{ getStatusText(heroActivity) }}
        </span>
        <div class="hero-card-label">推荐活动</div>
        <div class="hero-card-title">{{ heroActivity.subject }}</div>
        <div class="hero-card-desc">{{ getHeroTimeText(heroActivity) }}</div>
        <div class="hero-card-footer">
          <div class="hero-metric">
            <strong>{{ heroActivity.allSignUpCount || 0 }}</strong>
            <span>已报名</span>
          </div>
          <div class="hero-divider"></div>
          <div class="hero-metric">
            <strong>{{ heroActivity.signUpQuota || 0 }}</strong>
            <span>人数上限</span>
          </div>
        </div>
      </div>
    </div>

    <div class="activity-main">
      <div class="activity-search">
        <van-icon name="search" class="search-icon" />
        <input
          v-model.trim="keyword"
          type="text"
          placeholder="搜索活动标题"
          @click.stop
        />
        <span v-if="keyword" class="search-clear" @click.stop="keyword = ''">清除</span>
      </div>

      <div class="activity-list-header">
        <div>
          <div class="activity-list-title">精选活动</div>
          <div class="activity-list-subtitle">按活动期数倒序展示</div>
        </div>
        <div class="activity-list-count">{{ filteredActivityList.length }} 场</div>
      </div>

      <div v-if="filteredActivityList.length" class="activity-list">
        <div
          v-for="item in filteredActivityList"
          :key="item.id"
          class="activity-card"
          @click="goToDetail(item)"
        >
          <div class="activity-cover-box">
            <img :src="item.coverUrl || defaultCover" alt="" class="activity-cover" />
            <span class="activity-status" :class="getStatusTagClass(item)">
              {{ getStatusText(item) }}
            </span>
          </div>

          <div class="activity-body">
            <div class="activity-title">{{ item.subject }}</div>

            <div class="activity-meta">
              <div class="meta-item">
                <van-icon name="coupon-o" />
                <span>第{{ item.numberOfPeriods || "--" }}期活动</span>
              </div>
              <div class="meta-item">
                <van-icon name="clock-o" />
                <span>{{ getTimeRangeText(item) }}</span>
              </div>
            </div>

            <div class="activity-footer">
              <div class="join-count">
                已报名 <strong>{{ item.allSignUpCount || 0 }}</strong> 人
              </div>
              <button class="join-btn" @click.stop="goToDetail(item)">
                {{ getActionText(item) }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="activity-empty">
        <van-empty description="暂无匹配活动" />
      </div>
    </div>

    <TabBar active-key="activity" />
  </div>
</template>

<script>
import TabBar from "@/components/TabBar.vue";
import { mapGetters } from "vuex";
import { getAllActivityInfo, getUserSignedUpActivities } from "../api/activityInfo";

export default {
  components: { TabBar },
  data() {
    return {
      activityList: [],
      keyword: "",
      defaultCover: require("@/assets/defaultPhoto1.jpg")
    };
  },
  computed: {
    ...mapGetters(["baseInfo"]),
    filteredActivityList() {
      const keyword = (this.keyword || "").toLowerCase();
      if (!keyword) {
        return this.activityList;
      }
      return this.activityList.filter(item => {
        return (item.subject || "").toLowerCase().includes(keyword);
      });
    },
    heroActivity() {
      return this.activityList[0] || {};
    },
    availableActivityCount() {
      return this.activityList.filter(item => {
        return ["报名中", "进行中"].includes(this.getStatusText(item));
      }).length;
    },
    activitySummaryText() {
      if (!this.activityList.length) {
        return "暂未发布活动";
      }
      return `已发布${this.activityList.length}场活动 · 当前${this.availableActivityCount}场可参与`;
    }
  },
  methods: {
    // 解析后端返回的时间：去掉 Z 后缀，按本地时间解析，和后台设置的时间一致
    parseLocalTime(time) {
      if (!time) return null;
      let str = String(time);
      // 去掉时区后缀 Z 或 +00:00，避免被当作 UTC 解析后偏移8小时
      if (str.endsWith("Z")) str = str.slice(0, -1);
      if (str.endsWith("+00:00")) str = str.slice(0, -6);
      // 如果只有日期部分(无时间)，补全为 00:00:00
      if (str.length === 10) str = str + "T00:00:00";
      const d = new Date(str);
      return isNaN(d.getTime()) ? null : d;
    },
    goToDetail(item) {
      this.$router.push({ path: `/socialActivities/${item.id}` });
    },
    formatTime(time) {
      if (!time) return "";
      const date = this.parseLocalTime(time);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatDateTime(time) {
      if (!time) return "";
      const date = this.parseLocalTime(time);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hour = String(date.getHours()).padStart(2, "0");
      const minute = String(date.getMinutes()).padStart(2, "0");
      return `${month}-${day} ${hour}:${minute}`;
    },
    getOverdue(endTime) {
      if (!endTime) return false;
      const end = this.parseLocalTime(endTime); return end ? end > new Date() : false;
    },
    isInPeriod(startTime, endTime, fallbackEndTime) {
      const now = new Date();
      const start = startTime ? this.parseLocalTime(startTime) : null;
      const end = endTime ? this.parseLocalTime(endTime) : null;
    
      if (!start && !end) {
        return fallbackEndTime ? this.getOverdue(fallbackEndTime) : false;
      }
    
      if (start && now < start) return false;
      if (end && now > end) return false;
      return fallbackEndTime ? this.getOverdue(fallbackEndTime) : true;
    },
    isInSignUpPeriod(item) {
      return this.isInPeriod(item.signUpStartTime, item.signUpEndTime, item.endTime);
    },
    isInCheckInPeriod(item) {
      return this.isInPeriod(item.checkInStartTime, item.checkInEndTime, item.endTime);
    },
    isInMutualChoicePeriod(item) {
      return this.isInPeriod(item.mutualChoiceStartTime, item.mutualChoiceEndTime, null);
    },
        // 按报名/活动时间区间显示状态：
    // 未开始(报名开始前) / 报名中(报名开始至报名结束) / 进行中(活动开始至活动结束) / 已结束(活动结束后)
    getStatusText(item) {
      const now = new Date();
      const signUpStart = item.signUpStartTime ? this.parseLocalTime(item.signUpStartTime) : null;
      const signUpEnd = item.signUpEndTime ? this.parseLocalTime(item.signUpEndTime) : null;
      const start = item.startTime ? this.parseLocalTime(item.startTime) : null;
      const end = item.endTime ? this.parseLocalTime(item.endTime) : null;

      // 进行中优先级大于报名中：活动期内即使仍在报名期也显示进行中
      if (start && end && now >= start && now <= end) return "进行中";
      if (signUpStart && now < signUpStart) return "未开始";
      if (signUpEnd && now <= signUpEnd) return "报名中";
      // 报名结束至活动开始之间：活动尚未开始
      if (start && now < start) return "未开始";
      return "已结束";
    },
getStatusTagClass(item) {
      const text = this.getStatusText(item);
      switch (text) {
        case "已结束":
          return "tag-overdue";
        case "报名中":
          return "tag-signup";
        case "未开始":
          return "tag-upcoming";
        case "进行中":
          return "tag-ongoing";
        default:
          return "tag-signup";
      }
    },
    getTimeRangeText(item) {
      const status = this.getStatusText(item);

      if (item.signUpStartTime || item.signUpEndTime) {
        return `报名 ${this.formatDateTime(item.signUpStartTime)} - ${this.formatDateTime(item.signUpEndTime)}`;
      }

      return `报名 ${this.formatDateTime(item.signUpStartTime)} - ${this.formatDateTime(item.signUpEndTime)}`;
    },
    getHeroTimeText(item) {
      const timeText = this.getTimeRangeText(item);
      return timeText || "查看活动详情";
    },
    getActionText(item) {
      const status = this.getStatusText(item);
      if (status === "报名中") return "立即报名";
      return "查看详情";
    },
    async fillActivityProgress() {
      if (!this.baseInfo || !this.baseInfo.id || !this.activityList.length) {
        return;
      }

      const activityIds = this.activityList.map(item => item.id);
      try {
        const res = await getUserSignedUpActivities(activityIds, this.baseInfo.id);
        if (res.code === 20000 && res.data) {
          const progressMap = {};
          res.data.forEach(item => {
            progressMap[item.activityId] = item;
          });
          this.activityList = this.activityList.map(item => {
            const progress = progressMap[item.id];
            if (progress) {
              return {
                ...item,
                approvalStatus: progress.approvalStatus,
                checkInStatus: progress.checkInStatus,
                sceneCode: progress.sceneCode || ""
              };
            }
            return {
              ...item,
              approvalStatus: null,
              checkInStatus: 0,
              sceneCode: ""
            };
          });
        }
      } catch (error) {
        // 批量查询失败，保持默认状态
      }
    }
  },
  async created() {
    const res = await getAllActivityInfo();
    if (res.code === 20000) {
      this.activityList = res.data
        .filter(v => v.publishType === 1)
        .sort((a, b) => (b.numberOfPeriods || 0) - (a.numberOfPeriods || 0));
      await this.fillActivityProgress();
    }
  }
};
</script>

<style lang="less" src="../style/activityCenter.less" />



// WEBPACK FOOTER //
// src/views/activity.vue